import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, merge, throwError } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import {
  BillingEstimateActions,
  BillingEstimateSelectors,
  BillingPlansSelectors,
} from '@recruitee/billing-plans-data';
import { filterBoolean } from '@recruitee/common';
import { AddonType } from '@recruitee/subscription-types';

import { ProductOrderParamsIT } from '../pages/product-order/product-order.types';
import { PricingFeatureFacade } from '../types/pricing-feature.types';

@Injectable()
export class ProductOrderEstimateService {
  public estimate$ = this.store.select(BillingEstimateSelectors.data).pipe(filterBoolean);
  private isLoaded$ = this.store.select(BillingEstimateSelectors.isLoaded);
  private params$ = inject(ProductOrderParamsIT);

  constructor(
    private facade: PricingFeatureFacade,
    private store: Store,
    private actions$: Actions,
  ) {}

  public getData$() {
    return this.isLoaded$.pipe(
      switchMap(isLoaded => (isLoaded ? this.estimate$ : this.loadData$())),
    );
  }

  public loadData$() {
    this.fetch();
    const success = this.actions$
      .pipe(ofType(BillingEstimateActions.fetchSuccess))
      .pipe(map(action => action.payload));
    const error = this.actions$
      .pipe(ofType(BillingEstimateActions.fetchFailed))
      .pipe(switchMap(action => throwError(() => action.payload.error)));

    return merge(success, error).pipe(take(1));
  }

  private fetch() {
    const load$ = combineLatest({
      params: this.params$,
      activePlan: this.facade.getActiveBillingPlan(),
      billingPlans: this.facade.getBillingPlansWithActive(),
      sizeRange: this.params$.pipe(
        switchMap(params =>
          this.store.select(BillingPlansSelectors.rangeForQuantity(params.quantity)),
        ),
      ),
    }).pipe(
      take(1),
      switchMap(({ params, activePlan, billingPlans, sizeRange }) => {
        const { productId, quantity, interval, slots } = params;
        const selectedPlanId = productId;
        const selectedPlanTier = billingPlans.find(plan => plan.id === selectedPlanId)!.tier;
        const selectedPlanCustom = billingPlans.find(plan => plan.id === selectedPlanId)!.custom;
        const selectedPlanPeriod = interval;
        const activePlanTier = activePlan?.tier;
        const isChangingPlan = !activePlan || activePlanTier !== selectedPlanTier;
        const isChangingPeriod = !activePlan || activePlan.billingPeriod !== selectedPlanPeriod;
        const isSwitchingToCustom = !activePlan || (!activePlan.custom && selectedPlanCustom);
        const estimatePlanIdParam =
          isChangingPlan || isChangingPeriod || isSwitchingToCustom
            ? selectedPlanId
            : activePlan?.id;
        const addons =
          quantity !== undefined
            ? []
            : slots !== undefined
              ? [{ type: AddonType.Slots, quantity: slots }]
              : [];

        return this.facade.loadBillingEstimate(interval, estimatePlanIdParam, addons, sizeRange);
      }),
    );
    load$.pipe(take(1)).subscribe();
  }
}
